import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { LogOut, Plus, Pencil, Trash2, Upload, X, GripVertical, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPanel = () => {
  const [token, setToken] = useState(localStorage.getItem("sartaj_token") || "");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploading, setUploading] = useState(false);

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  // Check auth on mount
  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(() => { setIsLoggedIn(true); })
        .catch(() => { setToken(""); localStorage.removeItem("sartaj_token"); });
    }
  }, [token]);

  // Fetch products when logged in
  useEffect(() => {
    if (isLoggedIn) fetchProducts();
  }, [isLoggedIn]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/admin/products`, authHeaders());
      setProducts(res.data);
    } catch { /* ignore */ }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      const t = res.data.token;
      setToken(t);
      localStorage.setItem("sartaj_token", t);
      setIsLoggedIn(true);
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Login failed");
    }
  };

  const handleLogout = () => {
    setToken("");
    localStorage.removeItem("sartaj_token");
    setIsLoggedIn(false);
  };

  const handleImageUpload = async (file, productId) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      // Update product image
      if (productId) {
        await axios.put(`${API}/admin/products/${productId}`, { image: res.data.url }, authHeaders());
        fetchProducts();
      }
      return res.data.url;
    } catch {
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, authHeaders());
      fetchProducts();
    } catch { alert("Delete failed"); }
  };

  const handleSaveProduct = async (product) => {
    try {
      if (product.id) {
        await axios.put(`${API}/admin/products/${product.id}`, product, authHeaders());
      } else {
        await axios.post(`${API}/admin/products`, product, authHeaders());
      }
      fetchProducts();
      setEditingProduct(null);
      setShowAddForm(false);
    } catch { alert("Save failed"); }
  };

  // --- Login Screen ---
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-brand-bg-dark flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <h1 className="font-heading text-4xl font-light text-brand-gold tracking-tight">Sartaj Admin</h1>
            <p className="font-body text-sm text-brand-text-light/50 mt-2">Product Management Portal</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6" data-testid="admin-login-form">
            <div>
              <label className="block text-xs uppercase tracking-widest text-brand-gold/70 font-body mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                className="w-full bg-transparent border-b border-brand-gold/30 py-3 text-brand-text-light font-body focus:outline-none focus:border-brand-gold transition-colors"
                required
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-brand-gold/70 font-body mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="w-full bg-transparent border-b border-brand-gold/30 py-3 text-brand-text-light font-body focus:outline-none focus:border-brand-gold transition-colors"
                required
              />
            </div>
            {loginError && <p data-testid="admin-login-error" className="text-red-400 text-sm font-body">{loginError}</p>}
            <button
              type="submit"
              data-testid="admin-login-btn"
              className="w-full py-4 bg-brand-gold text-brand-primary text-xs uppercase tracking-[0.2em] font-body font-semibold hover:bg-brand-gold-light transition-colors"
            >
              Sign In
            </button>
          </form>
          <div className="text-center mt-8">
            <a href="/" className="text-xs text-brand-text-light/30 hover:text-brand-gold font-body transition-colors">
              Back to Website
            </a>
          </div>
        </div>
      </div>
    );
  }

  // --- Admin Dashboard ---
  return (
    <div className="min-h-screen bg-brand-bg-light" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-brand-primary border-b border-brand-gold/20 px-6 md:px-12 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <a href="/" className="text-brand-text-light/50 hover:text-brand-gold transition-colors">
            <ArrowLeft size={20} />
          </a>
          <h1 className="font-heading text-xl text-brand-gold font-light">Sartaj Admin</h1>
        </div>
        <button
          onClick={handleLogout}
          data-testid="admin-logout-btn"
          className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand-text-light/50 hover:text-brand-gold font-body transition-colors"
        >
          <LogOut size={16} /> Logout
        </button>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        {/* Title + Add Button */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-3xl font-light text-brand-primary">Product Management</h2>
            <p className="font-body text-sm text-brand-text-dark/50 mt-1">{products.length} varieties in your range</p>
          </div>
          <button
            onClick={() => { setShowAddForm(true); setEditingProduct(null); }}
            data-testid="add-product-btn"
            className="flex items-center gap-2 px-6 py-3 bg-brand-gold text-brand-primary text-xs uppercase tracking-[0.15em] font-body font-semibold hover:bg-brand-gold-light transition-colors"
          >
            <Plus size={16} /> Add Variety
          </button>
        </div>

        {/* Add/Edit Form */}
        {(showAddForm || editingProduct) && (
          <ProductForm
            product={editingProduct}
            onSave={handleSaveProduct}
            onCancel={() => { setShowAddForm(false); setEditingProduct(null); }}
            onUpload={handleImageUpload}
            uploading={uploading}
            token={token}
          />
        )}

        {/* Product List */}
        <div className="space-y-4">
          {products.map((product) => (
            <div
              key={product.id}
              data-testid={`admin-product-${product.id}`}
              className="flex items-center gap-4 border border-brand-primary/10 bg-white p-4"
            >
              <GripVertical size={16} className="text-brand-text-dark/20 flex-shrink-0" />
              {/* Image */}
              <div className="w-20 h-20 flex-shrink-0 bg-brand-primary/5 overflow-hidden">
                {product.image ? (
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Upload size={16} className="text-brand-text-dark/20" />
                  </div>
                )}
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-lg text-brand-primary truncate">{product.name}</h3>
                <p className="text-xs text-brand-gold font-body uppercase tracking-wider">{product.tagline}</p>
                {product.description && (
                  <p className="text-xs text-brand-text-dark/50 font-body mt-1 truncate">{product.description}</p>
                )}
              </div>
              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Upload image */}
                <label
                  data-testid={`upload-image-${product.id}`}
                  className="w-9 h-9 flex items-center justify-center border border-brand-primary/10 text-brand-text-dark/40 hover:text-brand-gold hover:border-brand-gold/30 cursor-pointer transition-colors"
                >
                  <Upload size={14} />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files[0], product.id)}
                  />
                </label>
                <button
                  onClick={() => { setEditingProduct(product); setShowAddForm(false); }}
                  data-testid={`edit-product-${product.id}`}
                  className="w-9 h-9 flex items-center justify-center border border-brand-primary/10 text-brand-text-dark/40 hover:text-brand-gold hover:border-brand-gold/30 transition-colors"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => handleDeleteProduct(product.id)}
                  data-testid={`delete-product-${product.id}`}
                  className="w-9 h-9 flex items-center justify-center border border-brand-primary/10 text-brand-text-dark/40 hover:text-red-500 hover:border-red-300 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// --- Product Form Component ---
const ProductForm = ({ product, onSave, onCancel, onUpload, uploading, token }) => {
  const [name, setName] = useState(product?.name || "");
  const [tagline, setTagline] = useState(product?.tagline || "");
  const [description, setDescription] = useState(product?.description || "");
  const [image, setImage] = useState(product?.image || "");
  const [sortOrder, setSortOrder] = useState(product?.sort_order || 0);

  const handleImageFile = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      setImage(res.data.url);
    } catch { alert("Upload failed"); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      ...(product?.id ? { id: product.id } : {}),
      name: name.trim(),
      tagline: tagline.trim(),
      description: description.trim(),
      image,
      sort_order: sortOrder,
    });
  };

  return (
    <div className="border border-brand-gold/20 bg-white p-6 mb-8" data-testid="product-form">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading text-xl text-brand-primary">
          {product ? "Edit Product" : "Add New Variety"}
        </h3>
        <button onClick={onCancel} className="text-brand-text-dark/40 hover:text-brand-primary">
          <X size={20} />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs uppercase tracking-widest text-brand-text-dark/50 font-body mb-2">Product Name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="product-name-input"
              className="w-full border border-brand-primary/10 py-3 px-4 font-body text-sm text-brand-text-dark focus:outline-none focus:border-brand-gold transition-colors"
              required
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-brand-text-dark/50 font-body mb-2">Tagline</label>
            <input
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              data-testid="product-tagline-input"
              className="w-full border border-brand-primary/10 py-3 px-4 font-body text-sm text-brand-text-dark focus:outline-none focus:border-brand-gold transition-colors"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-brand-text-dark/50 font-body mb-2">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="product-description-input"
            rows={3}
            className="w-full border border-brand-primary/10 py-3 px-4 font-body text-sm text-brand-text-dark focus:outline-none focus:border-brand-gold transition-colors resize-none"
          />
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs uppercase tracking-widest text-brand-text-dark/50 font-body mb-2">Image</label>
            <div className="flex gap-3">
              <input
                value={image}
                onChange={(e) => setImage(e.target.value)}
                placeholder="Paste image URL or upload"
                data-testid="product-image-input"
                className="flex-1 border border-brand-primary/10 py-3 px-4 font-body text-sm text-brand-text-dark focus:outline-none focus:border-brand-gold transition-colors"
              />
              <label className="flex items-center gap-2 px-4 py-3 border border-brand-primary/10 text-brand-text-dark/50 hover:text-brand-gold hover:border-brand-gold/30 cursor-pointer font-body text-xs uppercase tracking-wider transition-colors">
                <Upload size={14} /> Upload
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageFile(e.target.files[0])} />
              </label>
            </div>
            {image && (
              <div className="mt-3 w-24 h-24 border border-brand-primary/10 overflow-hidden">
                <img src={image} alt="Preview" className="w-full h-full object-cover" />
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-brand-text-dark/50 font-body mb-2">Sort Order</label>
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              data-testid="product-sort-input"
              className="w-full border border-brand-primary/10 py-3 px-4 font-body text-sm text-brand-text-dark focus:outline-none focus:border-brand-gold transition-colors"
            />
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            data-testid="save-product-btn"
            className="px-8 py-3 bg-brand-gold text-brand-primary text-xs uppercase tracking-[0.15em] font-body font-semibold hover:bg-brand-gold-light transition-colors"
          >
            {product ? "Update Product" : "Add Product"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-8 py-3 border border-brand-primary/10 text-brand-text-dark/50 text-xs uppercase tracking-[0.15em] font-body hover:border-brand-primary/30 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminPanel;
